import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { format } from "date-fns";
import { ChevronDown, MapPinOff, RefreshCw } from "lucide-react";
import { get } from "@/lib/api";
import { useAsync } from "@/lib/queries";
import { ADDRESS_STATUSES, formatDate, todayISO } from "@/lib/constants";
import { DatePicker } from "@/components/DatePicker";
import { STATUS_HEX, locksmithColor } from "@/lib/mapColors";
import { ErrorState, PageHeader } from "@/components/States";
import { UserAvatar, useAvatarUrls } from "@/components/UserAvatar";
import { TeamMap } from "@/components/map/TeamMap";
import { PinPopup } from "@/components/map/PinPopup";
import { Button } from "@/components/ui/button";

const flatten = (runs) => runs.flatMap((run) => run.addresses.map((a) => ({ run, a })));

const ColorToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="group" aria-label="Colour pins by">
    {[["locksmith", "Locksmith"], ["status", "Status"]].map(([v, label]) => (
      <button key={v} type="button" onClick={() => onChange(v)} aria-pressed={value === v} data-testid={`map-color-by-${v}`}
        className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${value === v ? "bg-white text-blue-800 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
        {label}
      </button>
    ))}
  </div>
);

const Dot = ({ color }) => <span className="h-3 w-3 shrink-0 rounded-full ring-2 ring-white" style={{ background: color }} />;

const LocksmithLegend = ({ items, hidden, toggle }) => (
  <ul className="space-y-1">
    {items.map((l) => (
      <li key={l.id}>
        <button type="button" onClick={() => toggle(l.id)} aria-pressed={!hidden.has(l.id)} data-testid={`map-legend-locksmith-${l.id}`}
          className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-opacity hover:bg-slate-50 ${hidden.has(l.id) ? "opacity-40" : ""}`}>
          <Dot color={l.color} />{l.stamp && <UserAvatar userId={l.id} stamp={l.stamp} name={l.name} className="h-6 w-6" testId={`map-legend-avatar-${l.id}`} />}<span className="flex-1 truncate font-medium text-slate-800">{l.name}</span>
          <span className="font-data text-xs text-slate-500">{l.count}</span>
        </button>
      </li>
    ))}
  </ul>
);

const StatusLegend = ({ counts }) => (
  <ul className="space-y-1">
    {ADDRESS_STATUSES.map((s) => (
      <li key={s.value} className="flex items-center gap-2 px-2 py-1.5 text-sm" data-testid={`map-legend-status-${s.value}`}>
        <Dot color={STATUS_HEX[s.value]} /><span className="flex-1 text-slate-800">{s.label}</span>
        <span className="font-data text-xs text-slate-500">{counts[s.value] || 0}</span>
      </li>
    ))}
  </ul>
);

const Unplaced = ({ items }) => items.length > 0 && (
  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="map-unplaced">
    <p className="flex items-center gap-2 text-sm font-semibold text-amber-900"><MapPinOff className="h-4 w-4" /> Couldn&apos;t place on map ({items.length})</p>
    <ul className="mt-2 space-y-1 text-sm text-amber-900">
      {items.map(({ run, a }) => <li key={a.id}>{a.full_address} · <span className="font-data font-semibold">{a.postcode}</span> <span className="text-amber-700">({run.locksmith.name})</span></li>)}
    </ul>
  </div>
);

function useMapData(data, colorBy, hidden, base) {
  const photos = useAvatarUrls((data?.runs || []).map((r) => ({ id: r.locksmith.id, stamp: r.locksmith.avatar_updated_at })));
  return useMemo(() => {
    const all = flatten(data?.runs || []);
    const legend = {};
    const counts = {};
    all.forEach(({ run, a }) => {
      const l = (legend[run.locksmith.id] ||= { id: run.locksmith.id, name: run.locksmith.name, stamp: run.locksmith.avatar_updated_at, color: locksmithColor(run.locksmith.id), count: 0 });
      l.count += 1;
      counts[a.status] = (counts[a.status] || 0) + 1;
    });
    const placed = all.filter(({ a }) => a.lat != null);
    const filterOn = colorBy === "locksmith";
    const points = placed.filter(({ run }) => !(filterOn && hidden.has(run.locksmith.id))).map(({ run, a }) => ({
      key: a.id, lat: a.lat, lng: a.lng, area: a.area, boundary: a.boundary, pulse: a.status === "in_progress", img: photos[run.locksmith.id],
      color: colorBy === "status" ? STATUS_HEX[a.status] : locksmithColor(run.locksmith.id),
      popup: <PinPopup run={run} address={a} base={base} />,
    }));
    const unplaced = all.filter(({ a }) => a.lat == null);
    return { points, unplaced, counts, fitKey: placed.map(({ a }) => a.id).join("|"), legend: Object.values(legend).sort((x, y) => x.name.localeCompare(y.name)), total: all.length };
  }, [data, colorBy, hidden, photos, base]);
}

const REFRESH_MS = 60000;

function useAutoRefresh(reload) {
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible") reload();
    }, REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);
}

const DateBar = ({ date, setDate }) => (
  <div className="flex flex-wrap items-center gap-2">
    <DatePicker value={date} onChange={(v) => v && setDate(v)} testId="map-date-picker" className="w-44" />
    <Button variant="outline" onClick={() => setDate(todayISO())} disabled={date === todayISO()} data-testid="map-today-button">Today</Button>
  </div>
);

export default function MapPage() {
  const { profile } = useAuth();
  const isLocksmith = profile?.role === "locksmith";
  if (profile?.company?.maps_enabled === false) return <Navigate to={isLocksmith ? "/jobs" : "/manager"} replace />;
  return <MapPageInner isLocksmith={isLocksmith} />;
}

function MapPageInner({ isLocksmith }) {
  const [date, setDate] = useState(todayISO());
  const { data, loading, error, reload } = useAsync(() => get("/map/today", { date }), [date]);
  const [updatedAt, setUpdatedAt] = useState(null);
  useAutoRefresh(reload);
  useEffect(() => {
    if (data) setUpdatedAt(new Date());
  }, [data]);
  const [colorBy, setColorBy] = useState(isLocksmith ? "status" : "locksmith");
  const [hidden, setHidden] = useState(() => new Set());
  const [legendOpen, setLegendOpen] = useState(false);
  const m = useMapData(data, colorBy, hidden, isLocksmith ? "/jobs" : "/manager/runs");
  const toggle = (id) => setHidden((h) => { const n = new Set(h); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="space-y-4" data-testid="map-page">
      <PageHeader eyebrow={formatDate(date)} title={isLocksmith ? "My Map" : "Map"} description={isLocksmith ? "Every address on your runs for the day, pinned by postcode. Pick a date to see another day." : "Every address on the day's warrant runs, pinned by postcode. Updates every minute."}
        actions={<div className="flex flex-wrap items-center gap-2">
          <DateBar date={date} setDate={setDate} />
          <Button variant="outline" onClick={reload} disabled={loading} data-testid="map-refresh-button"><RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        </div>} />
      {updatedAt && <p className="-mt-2 text-xs text-slate-500" data-testid="map-updated-at">Last updated {format(updatedAt, "HH:mm:ss")}</p>}
      {error && <ErrorState message={error} onRetry={reload} />}
      <div className={isLocksmith ? "grid gap-4" : "grid gap-4 lg:grid-cols-[1fr_280px]"}>
        <TeamMap points={m.points} fitKey={m.fitKey} className={isLocksmith ? "h-[60vh] min-h-[380px]" : "h-[60vh] min-h-[380px] lg:h-[calc(100vh-220px)]"}
          emptyText={data && !data.runs.length ? (date === todayISO() ? (isLocksmith ? "You have no runs today" : "No warrant runs today") : `No ${isLocksmith ? "runs for you" : "warrant runs"} on ${formatDate(date, "dd/MM/yyyy")}`) : m.total && !m.points.length && hidden.size ? "All locksmiths hidden" : null} />
        <aside className="h-fit space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="map-legend">
          {!isLocksmith && <ColorToggle value={colorBy} onChange={setColorBy} />}
          <button type="button" onClick={() => setLegendOpen(!legendOpen)} className="flex w-full items-center justify-between text-sm font-semibold text-slate-700 lg:hidden" data-testid="map-legend-toggle">
            Legend ({m.total} addresses) <ChevronDown className={`h-4 w-4 transition-transform ${legendOpen ? "rotate-180" : ""}`} />
          </button>
          <div className={`${legendOpen ? "block" : "hidden"} lg:block`}>
            {colorBy === "locksmith"
              ? (m.legend.length ? <LocksmithLegend items={m.legend} hidden={hidden} toggle={toggle} /> : <p className="text-sm text-slate-500">No locksmiths out on this day.</p>)
              : <StatusLegend counts={m.counts} />}
            {colorBy === "locksmith" && m.legend.length > 0 && <p className="mt-2 text-xs text-slate-500">Tap a locksmith to show or hide their pins.</p>}
            <p className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-600">
              <span className="ww-pin ww-pin-pulse ww-pin-legend" style={{ "--pin": "#f59e0b" }} /> In Progress (current job)
            </p>
          </div>
        </aside>
      </div>
      <Unplaced items={m.unplaced} />
    </div>
  );
}
